import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import { KycType } from "../../../api/enrichedStakeholder";
import {
  CIN,
  IDENTITY_DOCUMENT_TYPE_LABELS,
  IdentityDocumentType,
} from "../../../api/verificationsV2";
import { useAppDispatch, useAppSelector } from "../../../app/store";
import {
  IdentifierType,
  maskAadhaarForStorage,
  validateIdentifier,
} from "../../../components/ui/formelement/v2/EditableField";
import ManagementModal from "../../../components/ui/modals/ManagementModal";
import { showErrorToast, showSuccessToast } from "../../../lib/toast";
import ManagementFormField from "../../stakeholder/components/ManagementFormField";
import {
  getStakeholderBusinessDetails,
  getStakeholderKycDetails,
} from "../../stakeholder/logic/stakeholderSlice";
import {
  updateBusinessDetails,
  updateStakeholderKycDetails,
} from "../../stakeholder/logic/stakeholderThunks";

interface AddDocumentIdModalProps {
  stakeholderId: string;
  documentType: IdentityDocumentType;
  onClose: () => void;
}

const IDENTIFIER_TYPE_BY_DOCUMENT_TYPE: Partial<
  Record<IdentityDocumentType, IdentifierType>
> = {
  [KycType.PAN]: "pan",
  [KycType.AADHAAR]: "aadhaar",
  [CIN]: "cin",
  [KycType.PASSPORT]: "passport",
  [KycType.UDYAM_CERTIFICATE]: "udyam",
};

const AddDocumentIdModal: React.FC<AddDocumentIdModalProps> = ({
  stakeholderId,
  documentType,
  onClose,
}) => {
  const { id: caseId } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();

  const [documentId, setDocumentId] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const kycDetails = useAppSelector((state) =>
    getStakeholderKycDetails(state, stakeholderId),
  );
  const businessDetails = useAppSelector((state) =>
    getStakeholderBusinessDetails(state, stakeholderId),
  );

  const label = IDENTITY_DOCUMENT_TYPE_LABELS[documentType];

  const identifierType = IDENTIFIER_TYPE_BY_DOCUMENT_TYPE[documentType];
  const trimmedId = documentId.trim().toUpperCase();

  const formatError = identifierType
    ? validateIdentifier(identifierType, trimmedId)
    : null;

  const isSaveAllowed = trimmedId !== "" && !formatError && !isSaving;

  const saveToKycDetails = async (kyc_type: KycType) => {
    const kyc_value =
      documentType === KycType.AADHAAR
        ? maskAadhaarForStorage(trimmedId)
        : trimmedId;

    const existingIndex = kycDetails.findIndex(
      (detail) => detail.kyc_type === kyc_type,
    );

    const updatedKycDetails =
      existingIndex >= 0
        ? kycDetails.map((detail) =>
            detail.kyc_type === kyc_type ? { ...detail, kyc_value } : detail,
          )
        : [...kycDetails, { kyc_id: uuidv4(), kyc_type, kyc_value }];

    await dispatch(
      updateStakeholderKycDetails({
        caseId: caseId as string,
        stakeholderId,
        request: { kyc_details: updatedKycDetails },
      }),
    ).unwrap();
  };

  const saveToBusinessDetails = async () => {
    await dispatch(
      updateBusinessDetails({
        caseId: caseId as string,
        stakeholderId,
        request: { ...businessDetails, cin: trimmedId },
      }),
    ).unwrap();
  };

  const handleSubmit = async () => {
    if (!isSaveAllowed || !caseId) return;

    setIsSaving(true);

    try {
      if (documentType === CIN) {
        await saveToBusinessDetails();
      } else {
        await saveToKycDetails(documentType);
      }

      showSuccessToast(`${label} number saved`);
      onClose();
    } catch {
      showErrorToast(`Failed to save ${label} number. Please try again.`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ManagementModal
      title={`Add ${label} Number`}
      subtitle={label}
      subtitleClassName="text-[12px] leading-4 text-[#737373]"
      submitLabel="Save"
      submitEnabled={isSaveAllowed}
      isLoading={isSaving}
      onSubmit={handleSubmit}
      onClose={onClose}
      className="w-[480px]"
    >
      <ManagementFormField
        label={`${label} Number`}
        type={identifierType}
        required
        value={documentId}
        onChange={setDocumentId}
        disabled={isSaving}
        showError={trimmedId !== ""}
      />
    </ManagementModal>
  );
};

export default AddDocumentIdModal;
